import { skipToken } from "@reduxjs/toolkit/query";
import { collectPlaybookCriteria } from "@repo/app-types/entities/playbook";
import type { RecordingGradingAssessment } from "@repo/app-types/entities/recording";
import { parseGradingValue } from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { renderGradingValue } from "./gradingHelpers.js";

export interface GradingSectionSummariesContainerProps {
  assessment: RecordingGradingAssessment;
  selectedCriteriaId?: string;
  onCriteriaSelect?: (criteriaId: string) => void;
  mode?: "compact" | "clickable";
  emptyState?: ReactNode;
  className?: string;
}

// Helper function to generate abbreviation
function generateAbbreviation(sectionName: string, abbrLength = 3): string {
  // First, look for acronyms in parentheses - use that if we find one.
  const acronymMatch = sectionName.match(/\(([A-Z]{2,})\)/);
  if (acronymMatch && acronymMatch[1]) {
    return acronymMatch[1];
  }

  // If no acronym is found, clean the section name by removing parenthetical phrases and special characters, then replace slashes with spaces.
  // This prepares the string so we can later generate an abbreviation by picking the first letter of each word.
  const cleanedTitle = sectionName
    .replace(/\([^)]*\)/g, "")
    .replace(/[^\w\s/]/g, "")
    .replace(/\//g, " ")
    .trim();

  const words = cleanedTitle.split(/\s+/).filter((word) => word.length > 0);

  if (words.length === 0) {
    return sectionName.slice(0, abbrLength).toUpperCase();
  }

  if (words.length >= abbrLength) {
    return words
      .slice(0, abbrLength)
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
  }

  return words
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();
}

function getIndicatorStyles(parsedValue: any): string {
  if (!parsedValue || parsedValue.value === null) {
    return "bg-gray-100 border-border-strong"; // Not graded
  }

  if (parsedValue.type === "yes_no") {
    return parsedValue.value
      ? "bg-green-100 border-green-300"
      : "bg-red-100 border-red-300";
  }

  if (parsedValue.type === "1_5_score") {
    const score = parsedValue.value;
    if (score >= 4) return "bg-green-100 border-green-300";
    if (score >= 3) return "bg-gray-100 border-border-strong";
    if (score >= 2) return "bg-yellow-100 border-yellow-300";
    return "bg-red-100 border-red-300";
  }

  return "bg-gray-100 border-border-strong";
}

export const GradingSectionSummariesContainer: FC<
  GradingSectionSummariesContainerProps
> = (props) => {
  const {
    assessment,
    selectedCriteriaId,
    onCriteriaSelect,
    mode = "compact",
    emptyState,
    className,
  } = props;

  const { data: playbook, isLoading } = usePlaybookByIdQuery(
    assessment.recording_assessment_template_id ?? skipToken,
  );

  if (isLoading || !playbook) {
    return null;
  }

  // Filter for section nodes only
  const sectionNodes = playbook.assessment_tree.nodes.filter(
    (node) => node.type === "section",
  );

  if (sectionNodes.length === 0) {
    return (
      emptyState ?? (
        <div className="flex items-center gap-2">
          <div className="text-xs text-gray-400">No sections</div>
        </div>
      )
    );
  }

  const renderedSections = sectionNodes
    .map((section) => {
      const abbreviation = generateAbbreviation(section.name);
      const criteria = collectPlaybookCriteria(section);

      // Filter for graded criteria only
      const gradedCriteria = criteria
        .map((criterion) => {
          const grading = assessment.criteria_grading[criterion.id];
          const parsedValue = parseGradingValue(
            criterion.grading_value_type,
            grading?.value,
          );
          return { criterion, parsedValue };
        })
        .filter(({ parsedValue }) => {
          return parsedValue && parsedValue.value !== null;
        });

      // Don't render if no graded criteria
      if (gradedCriteria.length === 0) {
        return null;
      }

      return (
        <div
          key={section.id}
          className={twMerge(
            "flex items-center",
            mode === "clickable" ? "shrink-0 gap-2" : "gap-1",
          )}
        >
          <span className="text-xs font-semibold text-gray-600">
            {abbreviation}
          </span>
          <div
            className={twMerge(
              "flex items-center",
              mode === "clickable" ? "gap-1" : "gap-0.5",
            )}
          >
            {gradedCriteria.map(({ criterion, parsedValue }) => {
              const tooltipContent = (
                <div>
                  <div className="text-sm font-medium">{criterion.name}</div>
                  <div className="mt-1 text-xs text-gray-300">
                    {renderGradingValue(
                      criterion.grading_value_type,
                      assessment.criteria_grading[criterion.id]?.value,
                    )}
                  </div>
                </div>
              );

              const square = (
                <div
                  className={twMerge(
                    mode === "clickable" ? "h-4 w-4" : "h-2 w-2",
                    "rounded-xs border transition-all duration-200",
                    getIndicatorStyles(parsedValue),
                    onCriteriaSelect ? "cursor-pointer hover:scale-110" : "",
                    selectedCriteriaId === criterion.id
                      ? "ring-2 ring-blue-500 ring-offset-1"
                      : "",
                  )}
                  onClick={() => onCriteriaSelect?.(criterion.id)}
                />
              );

              return (
                <TooltipTrigger key={criterion.id}>
                  <TooltipHoverTarget>{square}</TooltipHoverTarget>
                  <SimpleTooltip>{tooltipContent}</SimpleTooltip>
                </TooltipTrigger>
              );
            })}
          </div>
        </div>
      );
    })
    .filter(Boolean);

  if (renderedSections.length === 0) {
    return emptyState ?? null;
  }

  return (
    <div
      className={twMerge(
        "flex min-h-6 items-center",
        mode === "clickable"
          ? "w-full max-w-full min-w-0 gap-4 overflow-x-auto pt-1 pb-3"
          : "gap-2",
        className,
      )}
    >
      {renderedSections}
    </div>
  );
};
